import type { z } from "zod";
import { apiErrorBodySchema } from "@ortura/shared";

// =============================================================================
// API-Client (Entscheidung 36): schlanker fetch-Wrapper mit Zod-Prüfung
// =============================================================================
// - Immer relativ unter /api (Entscheidung 38): Vite-Proxy lokal, CloudFront später.
// - Access Token aus der AuthSession, Mandant per Header X-Ortura-Tenant
//   (Entscheidung 29). Die API prüft den Header gegen die Mitgliedschaften –
//   der Client entscheidet nichts über Zugriff.
// - Antworten werden mit dem Schema aus @ortura/shared geprüft. Weicht die API
//   vom Vertrag ab, fällt das hier sofort auf statt irgendwo in der Oberfläche.
// - 503 + Retry-After (Aurora wacht in dev aus der Pause auf, ~75 s) wird
//   automatisch wiederholt.

export const TENANT_HEADER = "X-Ortura-Tenant";

// Fehler mit dem einheitlichen Fehlerformat der API. `code` ist stabil und
// eignet sich für Übersetzungen, `message` ist der deutsche Text der API.
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly issues?: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiRequest<T> {
  readonly method?: "GET" | "POST" | "PATCH" | "DELETE";
  // Pfad ohne /api, z. B. "/me"
  readonly path: string;
  readonly body?: unknown;
  // Aktiver Mandant (aus der URL /t/{subdomain} aufgelöst, Entscheidung 35)
  readonly tenantId?: string;
  // Schema der erwarteten Antwort. `z.ZodType<T>` ist ein Generic: der Typ T
  // des Ergebnisses wird aus dem übergebenen Schema abgeleitet.
  readonly schema: z.ZodType<T>;
}

export interface ApiClient {
  request<T>(request: ApiRequest<T>): Promise<T>;
}

interface ClientOptions {
  // Liefert ein gültiges Access Token (AuthSession.getAccessToken).
  readonly getAccessToken: () => Promise<string | null>;
  // Austauschbar für Tests.
  readonly fetchFn?: typeof fetch;
  readonly sleep?: (ms: number) => Promise<void>;
  // Wie lange 503-Antworten insgesamt abgewartet werden.
  readonly maxRetryMs?: number;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Retry-After in Sekunden (die API sendet Sekunden), begrenzt auf 1–15 s.
function retryDelayMs(response: Response): number {
  const seconds = Number(response.headers.get("Retry-After"));
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 15) * 1000 : 5000;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = undefined;
  try {
    body = await response.json();
  } catch {
    // keine JSON-Antwort (z. B. 404 vom API Gateway)
  }
  const parsed = apiErrorBodySchema.safeParse(body);
  if (parsed.success) {
    const { code, message, requestId, issues } = parsed.data.error;
    return new ApiError(response.status, code, message, requestId, issues);
  }
  return new ApiError(response.status, "http_error", `HTTP ${response.status}`);
}

export function createApiClient(options: ClientOptions): ApiClient {
  const fetchFn = options.fetchFn ?? ((input, init) => fetch(input, init));
  const sleep = options.sleep ?? defaultSleep;
  const maxRetryMs = options.maxRetryMs ?? 90_000;

  return {
    async request<T>({ method = "GET", path, body, tenantId, schema }: ApiRequest<T>): Promise<T> {
      const headers = new Headers({ Accept: "application/json" });
      const token = await options.getAccessToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      if (tenantId) headers.set(TENANT_HEADER, tenantId);
      if (body !== undefined) headers.set("Content-Type", "application/json");

      let waited = 0;
      // Schleife nur für 503-Wiederholungen; jeder andere Ausgang verlässt sie.
      for (;;) {
        let response: Response;
        try {
          response = await fetchFn(`/api${path}`, {
            method,
            headers,
            ...(body !== undefined && { body: JSON.stringify(body) }),
          });
        } catch {
          throw new ApiError(0, "network_error", "Server nicht erreichbar");
        }

        if (response.status === 503 && waited < maxRetryMs) {
          const delay = retryDelayMs(response);
          waited += delay;
          await sleep(delay);
          continue;
        }

        if (!response.ok) throw await toApiError(response);

        // 204 No Content: kein Body, das Schema entscheidet (z. B. z.undefined()).
        const data: unknown = response.status === 204 ? undefined : await response.json();
        return schema.parse(data);
      }
    },
  };
}
